import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/ui/Hero';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiClock, FiCalendar, FiShare2 } = FiIcons;

const Videos = () => {
  // Placeholder data - easy to update with real YouTube IDs later
  const featuredVideo = {
    id: 'featured-1',
    title: 'Love 180: The Vision',
    description: 'Discover the heart behind our ministry and how God is moving on college campuses across Florida.',
    youtubeId: 'dQw4w9WgXcQ', // Placeholder ID
    date: 'Oct 15, 2023',
    duration: '5:24'
  };

  const videoLibrary = [
    {
      id: 'vid-1',
      title: 'Campus Outreach Highlights',
      category: 'Outreach',
      date: 'Nov 12, 2023',
      duration: '3:45',
      youtubeId: 'placeholder1'
    },
    {
      id: 'vid-2',
      title: 'Testimony: Sarah\'s Story',
      category: 'Testimonies',
      date: 'Nov 05, 2023',
      duration: '8:12',
      youtubeId: 'placeholder2'
    },
    {
      id: 'vid-3',
      title: 'City Worship Night',
      category: 'Events',
      date: 'Oct 28, 2023',
      duration: '4:30',
      youtubeId: 'placeholder3'
    },
    {
      id: 'vid-4',
      title: 'Why We Go: Training Session',
      category: 'Training',
      date: 'Oct 10, 2023',
      duration: '45:20',
      youtubeId: 'placeholder4'
    },
    {
      id: 'vid-5',
      title: 'Gainesville Mission Trip',
      category: 'Missions',
      date: 'Sep 15, 2023',
      duration: '12:15',
      youtubeId: 'placeholder5'
    },
    {
      id: 'vid-6',
      title: 'Fall Retreat Recap',
      category: 'Events',
      date: 'Sep 01, 2023',
      duration: '2:50',
      youtubeId: 'placeholder6'
    }
  ];

  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Outreach', 'Testimonies', 'Events', 'Training'];

  const filteredVideos = activeCategory === 'All' 
    ? videoLibrary 
    : videoLibrary.filter(video => video.category === activeCategory);

  return (
    <div>
      <Hero
        title="Media & Teaching"
        description="Watch sermons, testimonies, and highlights from our outreaches across Florida. Be inspired by what God is doing."
        backgroundImage="https://images.unsplash.com/photo-1516280440614-6697288d5d38?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
      />

      {/* Featured Video Section */}
      <Section>
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
            <SafeIcon icon={FiPlay} className="w-6 h-6 mr-2 text-primary-600" />
            Featured Video
          </h2>
          <Card className="overflow-hidden shadow-xl">
            <div className="grid lg:grid-cols-3 gap-0">
              <div className="lg:col-span-2 bg-black aspect-video relative group">
                {/* YouTube Embed Placeholder */}
                <iframe 
                  className="w-full h-full absolute inset-0"
                  src={`https://www.youtube.com/embed/${featuredVideo.youtubeId}?rel=0`}
                  title={featuredVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="p-8 bg-white flex flex-col justify-center">
                <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold tracking-wide uppercase mb-4 w-fit">
                  Featured
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                  {featuredVideo.title}
                </h3>
                <p className="text-neutral-600 mb-6 leading-relaxed">
                  {featuredVideo.description}
                </p>
                <div className="flex items-center text-sm text-neutral-500 space-x-6 mt-auto">
                  <div className="flex items-center">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
                    {featuredVideo.date}
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                    {featuredVideo.duration}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </Section>

      {/* Video Library */}
      <Section background="gray">
        <div className="flex flex-col md:flex-row justify-between items-center mb-10">
          <h2 className="text-3xl font-bold text-neutral-900 mb-6 md:mb-0">Latest Videos</h2>
          
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-white text-neutral-600 hover:bg-primary-50 hover:text-primary-600'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredVideos.map((video, index) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="h-full overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col">
                <div className="relative aspect-video bg-neutral-900 group cursor-pointer">
                  {/* Thumbnail Placeholder - using YouTube thumbnail URL pattern */}
                  <img 
                    src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`} 
                    alt={video.title}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/640x360?text=Video+Thumbnail'; }} 
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 bg-primary-600/90 rounded-full flex items-center justify-center text-white group-hover:scale-110 transition-transform shadow-lg backdrop-blur-sm">
                      <SafeIcon icon={FiPlay} className="w-5 h-5 ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {video.duration}
                  </div>
                </div>
                
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-xs font-semibold text-primary-600 uppercase tracking-wider">
                      {video.category}
                    </span>
                    <span className="text-xs text-neutral-500 flex items-center">
                      <SafeIcon icon={FiCalendar} className="w-3 h-3 mr-1" />
                      {video.date}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-neutral-900 mb-2 line-clamp-2">
                    {video.title}
                  </h3>
                  
                  <div className="mt-auto pt-4 flex justify-between items-center border-t border-neutral-100">
                    <button className="text-sm font-medium text-neutral-600 hover:text-primary-600 transition-colors flex items-center">
                      Watch Now
                    </button>
                    <button 
                      className="text-neutral-400 hover:text-primary-600 transition-colors"
                      title="Share"
                    >
                      <SafeIcon icon={FiShare2} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Subscribe CTA */}
      <Section>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-neutral-900 mb-6">Stay Updated</h2>
          <p className="text-xl text-neutral-600 mb-8">
            Subscribe to our YouTube channel to get notified when we post new testimonies and teaching videos.
          </p>
          <Button 
            variant="primary" 
            size="lg"
            onClick={() => window.open('https://youtube.com', '_blank')}
          >
            <SafeIcon icon={FiIcons.FiYoutube} className="w-5 h-5 mr-2" />
            Subscribe on YouTube
          </Button>
        </div>
      </Section>
    </div>
  );
};

export default Videos;